import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit2, 
  User, 
  Building, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Briefcase,
  Calendar,
  FileText,
  Users,
  UserCheck,
  Target,
  TrendingUp,
  Activity,
  ArrowRight,
  Paperclip,
  Sparkles,
  Plus,
  Tag,
  AlertTriangle
} from 'lucide-react';
import AttachmentServiceV2 from '../../components/Attachments/AttachmentServiceV2';
import TagService from '../../components/Tags/TagService';
import { api } from '../../utils/api';
import entityRelationService from '../../services/entityRelationService';
import { toast } from 'react-hot-toast';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { getDetailViewFields, isFieldVisible, getFieldDisplayValue } from '../../utils/entityConfig';
import { customerLeadScout } from './CustomerLeadScout';
import CustomAttributesDisplay from '../../components/CustomAttributes/CustomAttributesDisplay';
import { extractCustomAttributeFields } from '../../components/CustomAttributes/utils';
import PersonMergeModal from './PersonMergeModal';

const CustomerDetail = ({ customer: propCustomer, onEdit, onBack, hiddenFields: propHiddenFields }) => {
  const { i18n, safeT } = useTranslationReady();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Load entity configuration with proper caching and loading states
  const { config: entityConfig, loading: configLoading, error: configError } = useEntityConfig('customer');
  
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(!propCustomer);
  const [error, setError] = useState(null);
  const lastFetchedIdRef = useRef(null);
  const lastLocationKeyRef = useRef(null);
  const [hiddenFields, setHiddenFields] = useState(propHiddenFields || []);
  const [showSalespersonDetail, setShowSalespersonDetail] = useState(false);
  const [associatedPersons, setAssociatedPersons] = useState([]);
  const [loadingAssociatedPersons, setLoadingAssociatedPersons] = useState(false);
  const [companyCheck, setCompanyCheck] = useState({ loading: true, exists: false, duplicates: [] });
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);
  const [entityRelations, setEntityRelations] = useState({ leads: [], opportunities: [], contracts: [], activities: [] });
  const [entityRelationsUnified, setEntityRelationsUnified] = useState([]);
  const lastRelationsLoadedForIdRef = useRef(null);
  const [enriching, setEnriching] = useState(false);
  
  // Lead Scout state
  const [scouting, setScouting] = useState(false);
  const [scoutResults, setScoutResults] = useState(null);
  const [showScoutResults, setShowScoutResults] = useState(false);
  
  // Customer Summary expand/collapse state
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  // Duplicate person detection state
  const [duplicatePersons, setDuplicatePersons] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  
  const customAttributeFields = useMemo(
    () => extractCustomAttributeFields(entityConfig),
    [entityConfig]
  );

  // Debug: Log entity relations state changes
  useEffect(() => {
    console.log('🔄 [CustomerDetail] entityRelations state changed:', {
      leadsCount: entityRelations.leads.length,
      leads: entityRelations.leads,
      opportunitiesCount: entityRelations.opportunities.length,
      contractsCount: entityRelations.contracts.length,
      activitiesCount: entityRelations.activities.length,
      fullState: entityRelations
    });
  }, [entityRelations]);

  // Load entity configuration including hidden fields
  useEffect(() => {
    // Check for undefined/null/empty array
    if (entityConfig && (propHiddenFields === undefined || propHiddenFields === null || (Array.isArray(propHiddenFields) && propHiddenFields.length === 0))) {
      // The entityConfig returned from useEntityConfig hook already has hiddenFields at root level
      const configHiddenFields = entityConfig.hiddenFields || [];
      
      if (Array.isArray(configHiddenFields)) {
        setHiddenFields(configHiddenFields);
        console.log('✅ CustomerDetail: Loaded hidden fields from config:', configHiddenFields);
      } else {
        console.warn('⚠️ CustomerDetail: hiddenFields is not an array:', configHiddenFields);
        setHiddenFields([]);
      }
    } else if (propHiddenFields) {
      setHiddenFields(propHiddenFields);
      console.log('✅ CustomerDetail: Using prop hiddenFields:', propHiddenFields);
    }
  }, [entityConfig, propHiddenFields]);

  const isFieldHidden = (fieldName) => {
    return hiddenFields.includes(fieldName);
  };

  // Check if field should be visible in detail based on entity config
  const isFieldVisibleInDetail = (fieldName) => {
    // First check if field is in hiddenFields array
    if (isFieldHidden(fieldName)) {
      return false;
    }
    
    // Then check entity config UI visibility settings
    // The entityConfig returned from useEntityConfig hook is already the config object
    if (entityConfig?.fields?.[fieldName]) {
      const fieldConfig = entityConfig.fields[fieldName];
      // If showInDetail is explicitly false, hide the field
      if (fieldConfig.ui?.showInDetail === false) {
        return false;
      }
    }
    
    return true;
  };

  // Get dynamically rendered fields from entity config (fields not already hardcoded)
  const getDynamicDetailFields = () => {
    if (!entityConfig) return [];
    
    // The entityConfig returned from useEntityConfig hook is already the config object
    if (!entityConfig?.fields) return [];
    
    // List of fields that are already hardcoded in the detail view
    const hardcodedFields = new Set([
      'CustomerID', 'CustomerCode', 'CombinationName', 'CustomerType', 'PersonID', 'CompanyID', 'PersonCompanyID',
      'FirstName', 'LastName', 'FirstName_Eng', 'LastName_Eng', 'PersonEmail', 'PersonPhone',
      'PersonTitle', 'Title', 'JobTitle', 'Department', 'NickName', 'CompanyName', 'CompanyName_Eng', 'Industry', 'CompanyAddress',
      'City', 'Country', 'CompanyPhone', 'Website', 'Branch', 'CompanyEmail', 'SalespersonID', 'Status', 'Priority',
      'Notes', 'CustomerSummary', 'CreatedAt', 'UpdatedAt', 'Attributes', 'attributes',
      'Tags', 'tags', 'Tag', 'tag'
    ]);
    
    // Get all detail fields from config
    const allDetailFields = getDetailViewFields(entityConfig);
    
    const customerInfo = customerData?.customer || {};
    const customerType = customerInfo.CustomerType;
    
      // Filter out hardcoded fields and custom attributes (handled separately)
    return allDetailFields.filter(field => {
      // Skip if already hardcoded
      if (hardcodedFields.has(field.name)) return false;
      
      // Skip custom attributes (handled by CustomAttributesDisplay)
      if (field._source === 'tenant_custom' || field._tenantSpecific) return false;
      
      // Skip if hidden
      if (isFieldHidden(field.name)) return false;
      
      // Must explicitly have showInDetail === true (not just not false)
      if (field.ui?.showInDetail !== true) return false;
      
      // Skip fields from "Personal Information" group - they should be in hardcoded section
      const fieldGroup = field.group || field.ui?.group || '';
      if (fieldGroup === 'Personal Information' && customerType === 'Person') return false;
      
      // Check database table to ensure Person fields only show for Person type
      // and Company fields only show for Company type
      const dbTable = field.database?.table;
      if (dbTable === 'Persons' && customerType !== 'Person') return false;
      if (dbTable === 'Companies' && customerType !== 'Company') return false;
      
      // Check conditional visibility
      if (!isFieldVisible(field, customerInfo)) return false;
      
      return true;
    });
  };

  // Render a dynamic detail field
  const renderDynamicDetailField = (field) => {
    const fieldName = field.name;
    const fieldConfig = field;
    const customerInfo = customerData?.customer || {};
    let value = customerInfo[fieldName];
    
    // Handle JSON objects/strings for Attributes or JSON fields
    if (fieldName === 'Attributes' || fieldName === 'attributes' || fieldConfig.dataType === 'json' || fieldConfig.dataType === 'object') {
      // If value is a string, try to parse it as JSON
      if (typeof value === 'string' && value.trim().startsWith('{')) {
        try {
          value = JSON.parse(value);
        } catch (e) {
          // If parsing fails, keep as string
        }
      }
      
      // If value is an object, format it nicely
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const formattedJson = JSON.stringify(value, null, 2);
        return (
          <div key={fieldName} className="mb-4">
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              {fieldConfig.displayName || fieldName}
            </label>
            <pre className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm text-gray-900 dark:text-gray-100 overflow-x-auto">
              {formattedJson}
            </pre>
          </div>
        );
      }
    }
    
    // Default rendering for other field types
    const displayName = fieldConfig.displayName || fieldName;
    const displayNameTH = fieldConfig.displayNameTH || displayName;
    const isThai = i18n?.language?.toLowerCase().startsWith('th');
    const label = isThai && displayNameTH ? displayNameTH : displayName;
    const displayValue = getFieldDisplayValue(fieldConfig, value, customerInfo);
    
    return (
      <div key={fieldName} className="mb-4">
        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
          {label}
        </label>
        <p className="text-gray-900 dark:text-gray-100">{displayValue || '-'}</p>
      </div>
    );
  };

  // Helper function to check if any field in a group is visible
  const hasVisibleFieldsInGroup = (fieldsInGroup) => {
    return fieldsInGroup.some(fieldName => !isFieldHidden(fieldName));
  };

  useEffect(() => {
    if (propCustomer) {
      // If propCustomer is passed directly (from Customers.js), wrap it in the expected structure
      if (propCustomer.CustomerID) {
        // This is a direct customer object from the list view
        // Check if it has salesperson fields directly in the customer object
        const salesperson = (propCustomer.SalespersonFirstName || propCustomer.SalespersonLastName) ? {
          UserID: propCustomer.SalespersonID,
          FirstName: propCustomer.SalespersonFirstName,
          LastName: propCustomer.SalespersonLastName,
          Email: propCustomer.SalespersonEmail,
          Phone: propCustomer.SalespersonPhone
        } : null;
        
        // For Person customers, ensure the associated company information is properly formatted
        let customerData = propCustomer;
        if (propCustomer.CustomerType === 'Person' && (propCustomer.PersonCompanyName || propCustomer.PersonCompanyName_Eng)) {
          // The data already includes the associated company information from the backend
          // No need to modify it as the backend query now includes these fields
        }
        
        const currentCustomerId = propCustomer.CustomerID;
        setCustomerData({ 
          customer: customerData, 
          salesperson: salesperson, 
          contracts: [] 
        });
        // Load entity relations when navigating from list (propCustomer path)
        if (currentCustomerId) {
          try {
            console.log('🔁 Loading relations (propCustomer path) for CustomerID:', currentCustomerId);
            loadEntityRelations(currentCustomerId);
          } catch (e) {
            console.warn('⚠️ Failed to load relations for propCustomer:', e?.message || e);
          }
        }
      } else {
        // This is already in the expected format (from API)
        setCustomerData(propCustomer);
        // Also attempt to load relations if ID is present inside the structure
        const maybeId = propCustomer?.customer?.CustomerID || propCustomer?.CustomerID;
        if (maybeId) {
          try {
            console.log('🔁 Loading relations (formatted prop) for CustomerID:', maybeId);
            loadEntityRelations(maybeId);
          } catch (e) {
            console.warn('⚠️ Failed to load relations for formatted prop:', e?.message || e);
          }
        }
      }
      setLoading(false);
    } else if (id) {
      // Always fetch fresh data when navigating via URL (especially after edit)
      // Use location.key to detect navigation changes (React Router changes key on each navigation)
      const locationChanged = location.key !== lastLocationKeyRef.current;
      const idChanged = lastFetchedIdRef.current !== id;
      
      if (idChanged || locationChanged) {
        console.log('🔄 Fetching/refreshing customer data for ID:', id, { idChanged, locationChanged });
        lastFetchedIdRef.current = id;
        lastLocationKeyRef.current = location.key;
        fetchCustomerDetails();
      }
    }
  }, [id, propCustomer, location.key]);

  // Force refresh when navigation state includes forceRefresh (from edit form)
  useEffect(() => {
    const refreshToken = location.state && location.state.forceRefresh;
    if (!refreshToken) return;
    const currentId = customerData?.customer?.CustomerID || id;
    if (currentId) {
      fetchCustomerDetails(currentId);
    }
  }, [location.state?.forceRefresh]);

  // Refresh on window focus to ensure latest data after returning from edit
  useEffect(() => {
    const onFocus = () => {
      const currentId = customerData?.customer?.CustomerID || id;
      if (currentId) fetchCustomerDetails(currentId);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [customerData?.customer?.CustomerID, id]);

  // Ensure relations load whenever customerData changes and we have an ID
  useEffect(() => {
    const currentId = customerData?.customer?.CustomerID;
    if (!currentId) return;
    if (lastRelationsLoadedForIdRef.current === currentId) return;
    console.log('🔁 Auto-loading relations based on customerData change. CustomerID:', currentId);
    lastRelationsLoadedForIdRef.current = currentId;
    loadEntityRelations(currentId);
    loadUnifiedRelations(currentId);
  }, [customerData?.customer?.CustomerID]);

  // Fetch associated persons when customer is a company
  useEffect(() => {
    if (customerData?.customer?.CustomerType === 'Company' && customerData?.customer?.CustomerID) {
      fetchAssociatedPersons(customerData.customer.CustomerID);
    }
  }, [customerData]);

  // Check for duplicate persons when customer is a Person type
  useEffect(() => {
    if (customerData?.customer?.CustomerType === 'Person' && customerData?.customer?.CustomerID) {
      fetchDuplicates(customerData.customer.CustomerID);
    }
  }, [customerData?.customer?.CustomerID, customerData?.customer?.CustomerType]);

  // Check if company exists as a customer for Person type customers
  useEffect(() => {
    if (customerData?.customer?.CustomerType === 'Person' && 
        (customerData?.customer?.CompanyName || customerData?.customer?.PersonCompanyName)) {
      checkCompanyExists();
    } else {
      setCompanyCheck({ loading: false, exists: false, duplicates: [] });
    }
  }, [customerData?.customer?.CompanyName, customerData?.customer?.PersonCompanyName, customerData?.customer?.CustomerType]);

  const fetchCustomerDetails = async (customerIdParam = null) => {
    try {
      setLoading(true);
      const customerIdToFetch = customerIdParam || id;
      if (!customerIdToFetch) {
        console.warn('⚠️ fetchCustomerDetails: No customer ID available');
        return;
      }
      const data = await api.get(`/customers/${customerIdToFetch}`);
      setCustomerData(data);
      
      // Load entity relations separately
      await loadEntityRelations(customerIdToFetch);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEntityRelations = async (customerId) => {
    try {
      console.log('🔍 [CustomerDetail] Loading entity relations for customer:', customerId);
      // Fetch all relations in one resilient call, then filter by type locally
      const allResp = await entityRelationService.getRelatedEntities('Customer', customerId, { direction: 'both', timeoutMs: 15000 });
      const all = Array.isArray(allResp) ? allResp : (allResp?.relations || []);

      const toType = (rel) => (rel.TargetEntityType || rel.targetEntityType || '').toString().toLowerCase();
      const fromType = (rel) => (rel.SourceEntityType || rel.sourceEntityType || '').toString().toLowerCase();
      const getId = (rel, pick) => rel[pick] || rel[pick.charAt(0).toLowerCase() + pick.slice(1)] || null;

      // Leads: gather unique IDs from both directions
      const leadIds = Array.from(new Set(all.map(rel => {
        if (fromType(rel) === 'customer' && toType(rel) === 'lead') {
          return getId(rel, 'TargetEntityID');
        }
        if (fromType(rel) === 'lead' && toType(rel) === 'customer') {
          return getId(rel, 'SourceEntityID');
        }
        return null;
      }).filter(Boolean)));

      let leads = [];
      if (leadIds.length > 0) {
        try {
          const leadDetailResults = await Promise.all(leadIds.map(async (leadId) => {
            try {
              const detail = await api.get(`/leads/${leadId}`);
              return detail?.lead || detail || { LeadID: leadId };
            } catch (e) {
              console.warn('      ⚠️ Failed to fetch lead detail for', leadId, e.message || e);
              return { LeadID: leadId, _fetchError: true };
            }
          }));
          leads = leadDetailResults;
        } catch (e) {
          leads = leadIds.map(id => ({ LeadID: id, _fetchError: true }));
        }
      }

      // Opportunities
      const opportunities = all.filter(rel => (fromType(rel) === 'customer' && toType(rel) === 'opportunity') || (fromType(rel) === 'opportunity' && toType(rel) === 'customer'))
        .map(rel => ({
          OpportunityID: (fromType(rel) === 'opportunity') ? getId(rel, 'SourceEntityID') : getId(rel, 'TargetEntityID'),
          OpportunityName: rel.OpportunityName || rel.Title,
          Stage: rel.Stage,
          Amount: rel.Amount,
          Currency: rel.Currency,
          Probability: rel.Probability,
          Description: rel.Description,
          ExpectedCloseDate: rel.ExpectedCloseDate
        }))
        .filter(item => item.OpportunityID);

      // Contracts (keep as raw relation rows if any)
      const contracts = all.filter(rel => (fromType(rel) === 'customer' && toType(rel) === 'contract') || (fromType(rel) === 'contract' && toType(rel) === 'customer'));

      // Activities
      const activities = all.filter(rel => (fromType(rel) === 'customer' && toType(rel) === 'activity') || (fromType(rel) === 'activity' && toType(rel) === 'customer'))
        .map(rel => ({
          ActivityID: (fromType(rel) === 'activity') ? getId(rel, 'SourceEntityID') : getId(rel, 'TargetEntityID'),
          Subject: rel.Subject,
          ActivityType: rel.ActivityType,
          Status: rel.Status,
          Priority: rel.Priority,
          Description: rel.Description,
          DueDate: rel.DueDate,
          SalespersonName: rel.SalespersonName
        }))
        .filter(item => item.ActivityID);

      console.log('🔍 [CustomerDetail] About to set entity relations state:', {
        leadsCount: leads.length,
        leads: leads,
        opportunitiesCount: opportunities.length,
        opportunities: opportunities,
        contractsCount: contracts.length,
        contracts: contracts,
        activitiesCount: activities.length,
        activities: activities
      });

      setEntityRelations({
        leads,
        opportunities,
        contracts,
        activities
      });

      console.log('✅ [CustomerDetail] Entity relations set in state successfully');
      
      // Verify state was set correctly (will show in next render)
      setTimeout(() => {
        console.log('🔍 [CustomerDetail] State verification (after setState):', {
          entityRelationsLeadsCount: entityRelations.leads.length,
          entityRelationsLeads: entityRelations.leads,
          displayLeadsWillBe: leads.length > 0 ? leads.length : 'fallback to customerData.leads'
        });
      }, 100);
    } catch (error) {
      console.error('❌ Failed to load entity relations:', error);
      // Don't set error state as this is supplementary data
    }
  };

  // Unified entity relations (similar to LeadDetail)
  const loadUnifiedRelations = async (customerId) => {
    try {
      console.log('🔍 [CustomerDetail] loadUnifiedRelations - fetching unified relations for Customer', customerId);
      const relationsResp = await entityRelationService.getRelatedEntities('Customer', customerId, { direction: 'both', timeoutMs: 12000 });
      const relations = Array.isArray(relationsResp) ? relationsResp : (relationsResp?.relations || []);
      const truncate = (text, max = 60) => {
        if (!text) return '';
        const trimmed = String(text).trim();
        return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
      };

      const mapped = relations.map(rel => {
        const srcTypeRaw = rel.SourceEntityType || rel.sourceEntityType;
        const tgtTypeRaw = rel.TargetEntityType || rel.targetEntityType;
        const srcId = rel.SourceEntityID || rel.sourceEntityID;
        const tgtId = rel.TargetEntityID || rel.targetEntityID;
        const isCustomerSource = (srcTypeRaw || '').toString().toLowerCase() === 'customer';
        const entityTypeRaw = isCustomerSource ? tgtTypeRaw : srcTypeRaw;
        const entityTypeLower = (entityTypeRaw || '').toString().toLowerCase();
        const entityType = entityTypeLower === 'customer' ? 'Customer'
          : entityTypeLower === 'lead' ? 'Lead'
          : entityTypeLower === 'opportunity' ? 'Opportunity'
          : entityTypeLower === 'activity' ? 'Activity'
          : entityTypeLower === 'salenote' || entityTypeLower === 'salesnote' ? 'SaleNote'
          : (entityTypeRaw || '');
        const entityId = isCustomerSource ? tgtId : srcId;

        let entityName = '';
        let description = '';
        let status = '';
        let amount = '';
        
        switch (entityType) {
          case 'Customer':
            entityName = rel.CustomerName || `${rel.FirstName || ''} ${rel.LastName || ''}`.trim() || rel.CompanyName || `Customer ${entityId}`;
            // Store CustomerType if available in relation data (will be enriched later if not available)
            if (rel.CustomerType) {
              return {
                EntityType: entityType,
                EntityID: entityId,
                EntityName: entityName,
                Description: description,
                Status: status,
                Amount: amount,
                RelationID: rel.RelationID || rel.relationId,
                RelationType: rel.RelationType || rel.relationType,
                CustomerType: rel.CustomerType,
                _rawRel: rel
              };
            }
            break;
          case 'Lead':
            entityName = rel.Title || rel.ContactName || rel.CompanyName || `Lead ${entityId}`;
            description = rel.Description || '';
            status = rel.Status || '';
            break;
          case 'Opportunity':
            entityName = rel.OpportunityName || rel.Title || `Opportunity ${entityId}`;
            description = rel.Description || '';
            status = rel.Stage || '';
            amount = rel.Amount ? `${rel.Currency || 'USD'} ${Number(rel.Amount).toLocaleString()}` : '';
            break;
          case 'Activity':
            entityName = rel.Subject || `${rel.ActivityType || 'Activity'} ${entityId}`;
            description = rel.Description || '';
            status = rel.Status || '';
            break;
          case 'SaleNote':
            const noteText = rel.NoteText || '';
            entityName = truncate(noteText.split('\n')[0] || '', 40) || `Sale Note ${entityId}`;
            description = truncate(noteText, 100);
            break;
          default:
            entityName = `${entityType} ${entityId}`;
        }

        return {
          EntityType: entityType,
          EntityID: entityId,
          EntityName: entityName,
          Description: description,
          Status: status,
          Amount: amount,
          RelationID: rel.RelationID || rel.relationId,
          RelationType: rel.RelationType || rel.relationType,
          // Store CustomerType if available in relation data
          CustomerType: entityType === 'Customer' ? (rel.CustomerType || null) : undefined,
          // Store raw relation data for enrichment
          _rawRel: rel
        };
      });

      // Enrich all relations with additional details from API
      const enriched = await Promise.all(mapped.map(async (m) => {
        try {
          // For Customer entities, always enrich to get CustomerType if not already available
          if (m.EntityType === 'Customer' && !m.CustomerType) {
            try {
              const detail = await api.get(`/customers/${m.EntityID}`);
              const cust = detail?.customer || detail || {};
              const customerType = cust.CustomerType || m._rawRel?.CustomerType || 'Company';
              const name = customerType === 'Person'
                ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                : cust.CompanyName || cust.CustomerName;
              return { ...m, EntityName: name || m.EntityName, CustomerType: customerType };
            } catch (e) {
              console.warn('⚠️ [CustomerDetail] Failed to enrich Customer relation:', e);
              return { ...m, CustomerType: m._rawRel?.CustomerType || 'Company' };
            }
          }
          
          // Only fetch if we don't have description or need more details
          const needsEnrichment = !m.EntityName || m.EntityName === `${m.EntityType} ${m.EntityID}` || 
                                 (m.EntityType === 'Lead' && !m.Description) ||
                                 (m.EntityType === 'SaleNote' && !m.Description);
          
          if (!needsEnrichment) return m;

          if (m.EntityType === 'Lead') {
            const detail = await api.get(`/leads/${m.EntityID}`);
            const ld = detail?.lead || detail || {};
            return {
              ...m,
              EntityName: ld.Title || ld.ContactName || ld.CompanyName || m.EntityName,
              Description: ld.Description || m.Description,
              Status: ld.Status || m.Status
            };
          } else if (m.EntityType === 'Opportunity') {
            const detail = await api.get(`/opportunities/${m.EntityID}`);
            const opp = detail?.opportunity || detail || {};
            return {
              ...m,
              EntityName: opp.OpportunityName || opp.CustomerName || m.EntityName,
              Description: opp.Description || m.Description,
              Status: opp.Stage || m.Status,
              Amount: opp.Amount ? `${opp.Currency || 'USD'} ${Number(opp.Amount).toLocaleString()}` : m.Amount
            };
          } else if (m.EntityType === 'Activity') {
            const detail = await api.get(`/activities/${m.EntityID}`);
            const act = detail?.activity || detail || {};
            return {
              ...m,
              EntityName: act.Subject || `${act.ActivityType || 'Activity'} ${m.EntityID}` || m.EntityName,
              Description: act.Description || m.Description,
              Status: act.Status || m.Status
            };
          } else if (m.EntityType === 'SaleNote') {
            try {
              const detail = await api.get(`/sales-notes/${m.EntityID}`);
              const sn = detail?.saleNote || detail || {};
              const noteText = sn.NoteText || '';
              return {
                ...m,
                EntityName: truncate(noteText.split('\n')[0] || '', 40) || m.EntityName,
                Description: truncate(noteText, 100) || m.Description
              };
            } catch {
              return m;
            }
          }
        } catch (e) {
          console.warn('⚠️ [CustomerDetail] Failed to enrich relation for', m, e);
          return m;
        }
        return m;
      }));
      
      setEntityRelationsUnified(enriched);
    } catch (e) {
      console.warn('[CustomerDetail] Failed to load unified entity relations:', e);
      setEntityRelationsUnified([]);
    }
  };

  // Helpers for entity relations UI (icons, paths, and colors)
  const getEntityColors = (entityType, customerType = null) => {
    // For Customer entities, use different colors based on CustomerType
    if (entityType === 'Customer' && customerType) {
      if (customerType === 'Person') {
        // Customer (Person) = blue
        return {
          bg: 'bg-teal-50 dark:bg-teal-900/20',
          border: 'border-teal-200 dark:border-teal-800',
          hoverBorder: 'hover:border-teal-400 dark:hover:border-teal-600',
          icon: 'text-teal-600 dark:text-teal-400',
          badge: 'bg-teal-100 dark:bg-blue-900 text-teal-800 dark:text-teal-200'
        };
      } else {
        // Customer (Company) = green
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBorder: 'hover:border-green-400 dark:hover:border-green-600',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      }
    }
    
    switch (entityType) {
      case 'Customer':
        // Default Customer (fallback) = green (Company)
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBorder: 'hover:border-green-400 dark:hover:border-green-600',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      case 'Lead':
        // Lead = red
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          hoverBorder: 'hover:border-red-400 dark:hover:border-red-600',
          icon: 'text-red-600 dark:text-red-400',
          badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
        };
      case 'Opportunity':
        // Opportunity = violet
        return {
          bg: 'bg-violet-50 dark:bg-violet-900/20',
          border: 'border-violet-200 dark:border-violet-800',
          hoverBorder: 'hover:border-violet-400 dark:hover:border-violet-600',
          icon: 'text-violet-600 dark:text-violet-400',
          badge: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200'
        };
      case 'Activity':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          hoverBorder: 'hover:border-orange-400 dark:hover:border-orange-600',
          icon: 'text-orange-600 dark:text-orange-400',
          badge: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
        };
      case 'SaleNote':
        // SaleNote = yellow
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          hoverBorder: 'hover:border-yellow-400 dark:hover:border-yellow-600',
          icon: 'text-yellow-600 dark:text-yellow-400',
          badge: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-700',
          border: 'border-gray-200 dark:border-gray-700',
          hoverBorder: 'hover:border-gray-400 dark:hover:border-gray-600',
          icon: 'text-gray-600 dark:text-gray-400',
          badge: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
        };
    }
  };

  const getEntityIcon = (entityType) => {
    const colors = getEntityColors(entityType);
    const iconClass = `h-4 w-4 ${colors.icon}`;
    
    switch (entityType) {
      case 'Customer': return <Building className={iconClass} />;
      case 'Lead': return <User className={iconClass} />;
      case 'Opportunity': return <Target className={iconClass} />;
      case 'Activity': return <Calendar className={iconClass} />;
      case 'SaleNote': return <FileText className={iconClass} />;
      default: return <FileText className={iconClass} />;
    }
  };

  const getEntityPath = (entityType, entityId) => {
    switch (entityType) {
      case 'Customer': return `/customers/${entityId}`;
      case 'Lead': return `/leads/${entityId}`;
      case 'Opportunity': return `/opportunities/${entityId}`;
      case 'Activity': return `/activities/${entityId}`;
      case 'SaleNote': return `/notes?noteId=${entityId}`;
      default: return '#';
    }
  };

  const fetchAssociatedPersons = async (customerId) => {
    try {
      setLoadingAssociatedPersons(true);
      const data = await api.get(`/customers/${customerId}/associated-persons`);
      const primary = data.associatedPersons || [];
      if (Array.isArray(primary) && primary.length > 0) {
        setAssociatedPersons(primary);
      } else {
        // Fallback: fetch persons from customers list and filter by company id
        const companyId = data?.company?.CompanyID || customerData?.customer?.CompanyID;
        if (companyId) {
          try {
            const params = new URLSearchParams({
              page: 1,
              limit: 200,
              type: 'Person',
              sortBy: 'CreatedAt',
              sortOrder: 'DESC'
            });
            const selectedRole = localStorage.getItem('selectedRole');
            const res = await fetch(`/api/customers?${params.toString()}`, {
              headers: { 
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
              }
            });
            if (res.ok) {
              const list = await res.json();
              const persons = (list.customers || []).filter(p => (
                (p.PersonCompanyID && Number(p.PersonCompanyID) === Number(companyId)) ||
                (p.CompanyID && Number(p.CompanyID) === Number(companyId))
              )).map(p => ({
                PersonID: p.PersonID,
                FirstName: p.FirstName,
                LastName: p.LastName,
                FirstName_Eng: p.FirstName_Eng,
                LastName_Eng: p.LastName_Eng,
                Title: p.PersonTitle || p.Title,
                JobTitle: p.JobTitle,
                Email: p.PersonEmail || p.Email,
                Phone: p.PersonPhone || p.Phone,
                CustomerID: p.CustomerID,
                CustomerCode: p.CustomerCode,
                SalespersonFirstName: p.SalespersonFirstName,
                SalespersonLastName: p.SalespersonLastName,
                SalespersonEmail: p.SalespersonEmail
              }));
              setAssociatedPersons(persons);
            } else {
              setAssociatedPersons([]);
            }
          } catch (e) {
            setAssociatedPersons([]);
          }
        } else {
          setAssociatedPersons([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch associated persons:', err);
      setAssociatedPersons([]);
    } finally {
      setLoadingAssociatedPersons(false);
    }
  };

  const fetchDuplicates = async (customerId) => {
    try {
      setLoadingDuplicates(true);
      const data = await api.get(`/customers/${customerId}/check-duplicates`);
      if (data.hasDuplicates && Array.isArray(data.duplicates)) {
        setDuplicatePersons(data.duplicates);
      } else {
        setDuplicatePersons([]);
      }
    } catch (err) {
      console.error('Failed to fetch duplicates:', err);
      setDuplicatePersons([]);
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const checkCompanyExists = async () => {
    try {
      setCompanyCheck({ loading: true, exists: false, duplicates: [] });
      
      if (!customerData?.customer) {
        setCompanyCheck({ loading: false, exists: false, duplicates: [] });
        return;
      }

      const customer = customerData.customer;
      const companyName = customer.CompanyName || customer.PersonCompanyName;
      const companyNameEng = customer.CompanyName_Eng || customer.PersonCompanyName_Eng;
      
      if (!companyName) {
        setCompanyCheck({ loading: false, exists: false, duplicates: [] });
        return;
      }

      const duplicates = [];
      
      // Check by Thai company name
      const selectedRole = localStorage.getItem('selectedRole');
      const nameResponse = await fetch(`/api/customers/search/${encodeURIComponent(companyName)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });
      
      if (nameResponse.ok) {
        const nameData = await nameResponse.json();
        if (nameData.results && nameData.results.length > 0) {
          nameData.results.forEach(cust => {
            // Only include Company type customers
            if (cust.CustomerType === 'Company') {
              duplicates.push({
                ...cust,
                matchType: 'name',
                matchField: companyName
              });
            }
          });
        }
      }
      
      // Check by English company name if available and no Thai name match
      if (duplicates.length === 0 && companyNameEng) {
        const engNameResponse = await fetch(`/api/customers/search/${encodeURIComponent(companyNameEng)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        
        if (engNameResponse.ok) {
          const engNameData = await engNameResponse.json();
          if (engNameData.results && engNameData.results.length > 0) {
            engNameData.results.forEach(cust => {
              if (cust.CustomerType === 'Company' && !duplicates.find(d => d.CustomerID === cust.CustomerID)) {
                duplicates.push({
                  ...cust,
                  matchType: 'name_eng',
                  matchField: companyNameEng
                });
              }
            });
          }
        }
      }
      
      setCompanyCheck({
        loading: false,
        exists: duplicates.length > 0,
        duplicates: duplicates
      });
    } catch (err) {
      console.error('Error checking company existence:', err);
      setCompanyCheck({ loading: false, exists: false, duplicates: [] });
    }
  };

  const handleCreateCompanyCustomer = async () => {
    try {
      setAddingCompany(true);
      
      const customer = customerData.customer;
      const companyName = customer.CompanyName || customer.PersonCompanyName;
      const companyNameEng = customer.CompanyName_Eng || customer.PersonCompanyName_Eng;
      const industry = customer.Industry || customer.PersonCompanyIndustry;
      
      const companyData = {
        customerType: 'Company',
        company: {
          name: companyName,
          name_eng: companyNameEng || null,
          industry: industry || null,
          website: customer.Website || null
        }
      };
      
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch('/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: JSON.stringify(companyData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create company customer');
      }
      
      const result = await response.json();
      
      setShowAddCompanyModal(false);
      
      // Recheck company status
      await checkCompanyExists();
      
      alert(safeT('customers.companyCreatedSuccess', 'Company added to customers successfully!'));
      
      // Navigate to the new company customer
      if (result.customerId) {
        navigate(`/customers/${result.customerId}`);
      }
    } catch (err) {
      console.error('Error creating company customer:', err);
      alert(safeT('customers.companyCreatedError', 'Failed to add company to customers. Please try again.'));
    } finally {
      setAddingCompany(false);
    }
  };

  const handlePersonClick = (person) => {
    navigate(`/customers/${person.CustomerID}`);
  };

  const handleCreateNewPerson = () => {
    const company = customerData?.customer;
    if (!company || company.CustomerType !== 'Company') return;

    navigate('/customers/new', {
      state: {
        customerType: 'Person',
        preselectedCompany: {
          companyId: company.CustomerID,
          companyName: company.CompanyName,
          companyName_eng: company.CompanyName_Eng
        }
      }
    });
  };

  const handleCompanyClick = async (companyId) => {
    try {
      const response = await api.get(`/customers/company/${companyId}`);
      
      if (response.exists && response.customer) {
        // Company exists as a Customer record, navigate to it
        navigate(`/customers/${response.customer.CustomerID}`);
      } else if (response.company) {
        // Company exists but not as a Customer record
        const companyName = response.company.CompanyName || response.company.CompanyName_Eng;
        const shouldCreate = window.confirm(
          `"${companyName}" is not yet added as a customer.\n\n` +
          `Would you like to add it to your customers list?`
        );
        
        if (shouldCreate) {
          // Create company as customer
          try {
            const selectedRole = localStorage.getItem('selectedRole');
            const createResponse = await fetch('/api/customers', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
              },
              body: JSON.stringify({
                customerType: 'Company',
                company: {
                  name: response.company.CompanyName,
                  name_eng: response.company.CompanyName_Eng || null,
                  industry: response.company.Industry || null,
                  website: response.company.Website || null,
                  phone: response.company.Phone || null,
                  address: response.company.Address || null,
                  city: response.company.City || null,
                  country: response.company.Country || null
                },
                companyId: companyId
              })
            });
            
            if (!createResponse.ok) {
              throw new Error('Failed to create company customer');
            }
            
            const result = await createResponse.json();
            toast.success('Company added to customers successfully!');
            
            // Navigate to the new company customer
            if (result.customerId) {
              navigate(`/customers/${result.customerId}`);
            }
          } catch (createError) {
            console.error('Error creating company customer:', createError);
            toast.error('Failed to add company to customers. Please try again.');
          }
        }
      }
    } catch (err) {
      console.error('Failed to get company:', err);
      toast.error('Failed to load company information');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // URL helpers for consistent normalization and shorter display
  const normalizeHrefUrl = (url) => {
    if (!url) return '#';
    return /^https?:\/\//i.test(url) ? url : `http://${url}`;
  };

  const shortenUrlDisplay = (url, maxLen = 48) => {
    if (!url) return '';
    try {
      const href = normalizeHrefUrl(url);
      const parsed = new URL(href);
      const host = parsed.hostname;
      const path = (parsed.pathname || '/').replace(/\/+/g, '/');
      const base = `${host}${path}`;
      if (base.length <= maxLen) {
        return base + (parsed.search ? '…' : '');
      }
      const segments = path.split('/').filter(Boolean);
      if (segments.length === 0) return host;
      const first = segments[0];
      let display = `${host}/${first}`;
      if (display.length > maxLen) {
        return display.slice(0, maxLen - 1) + '…';
      }
      return display + '/…';
    } catch {
      return url.length > maxLen ? url.slice(0, maxLen - 1) + '…' : url;
    }
  };

  // Render text content with URLs converted to hyperlinks and smart shortening
  const renderTextWithLinks = (text) => {
    if (!text || typeof text !== 'string') return text;
    const urlRegex = /(https?:\/\/[\w.-]+(?:\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?|www\.[\w.-]+(?:\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?)/gi;

    const normalizeHref = (u) => {
      if (!u) return '#';
      return /^https?:\/\//i.test(u) ? u : `http://${u}`;
    };

    const shortenDisplay = (u) => {
      try {
        const href = normalizeHref(u);
        const parsed = new URL(href);
        const host = parsed.hostname;
        const path = (parsed.pathname || '/').replace(/\/+/g, '/');
        const base = `${host}${path}`;
        // Limit display length smartly
        const maxLen = 48;
        if (base.length <= maxLen) {
          return base + (parsed.search ? '…' : '');
        }
        // Keep host and first path segment
        const segments = path.split('/').filter(Boolean);
        if (segments.length === 0) return host;
        const first = segments[0];
        let display = `${host}/${first}`;
        if (display.length > maxLen) {
          return display.slice(0, maxLen - 1) + '…';
        }
        return display + '/…';
      } catch (e) {
        // Fallback: simple truncate
        const maxLen = 48;
        return u.length > maxLen ? u.slice(0, maxLen - 1) + '…' : u;
      }
    };

    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      const url = match[0];
      const start = match.index;
      const end = urlRegex.lastIndex;
      if (start > lastIndex) {
        const preceding = text.slice(lastIndex, start);
        // Preserve newlines
        preceding.split('\n').forEach((line, idx, arr) => {
          parts.push(<span key={`t-${lastIndex}-${start}-${idx}`}>{line}</span>);
          if (idx < arr.length - 1) parts.push(<br key={`br-${lastIndex}-${start}-${idx}`} />);
        });
      }
      const href = normalizeHref(url);
      const display = shortenDisplay(url);
      parts.push(
        <a
          key={`a-${start}-${end}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-teal-600 dark:text-teal-400 hover:underline break-all"
          title={href}
        >
          {display}
        </a>
      );
      lastIndex = end;
    }
    if (lastIndex < text.length) {
      const tail = text.slice(lastIndex);
      tail.split('\n').forEach((line, idx, arr) => {
        parts.push(<span key={`t-tail-${lastIndex}-${idx}`}>{line}</span>);
        if (idx < arr.length - 1) parts.push(<br key={`br-tail-${lastIndex}-${idx}`} />);
      });
    }
    return parts;
  };

  // Helper to truncate customer summary text
  const truncateSummary = (summaryText, maxLength = 500) => {
    if (!summaryText || summaryText.length <= maxLength) return summaryText;
    
    // Find a good breaking point (end of sentence or word)
    let truncated = summaryText.substring(0, maxLength);
    const lastPeriod = truncated.lastIndexOf('.');
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastPeriod > maxLength * 0.7) {
      truncated = summaryText.substring(0, lastPeriod + 1);
    } else if (lastSpace > maxLength * 0.7) {
      truncated = summaryText.substring(0, lastSpace);
    }
    
    return truncated;
  };

  // Helper to check if summary is long (more than 500 characters)
  const isSummaryLong = (summaryText) => {
    if (!summaryText || summaryText.length <= 500) return false;
    
    // Truncate the summary to see how much would be shown
    const truncated = truncateSummary(summaryText, 500);
    const remainingLength = summaryText.length - truncated.length;
    
    // If remaining content is less than 500 characters, show all instead of truncating
    if (remainingLength < 500) {
      return false;
    }
    
    return true;
  };

  const formatCustomerName = (customer) => {
    if (!customer) {
      return '';
    }
    if (customer.CustomerType === 'Person') {
      return `${customer.FirstName || ''} ${customer.LastName || ''}`.trim();
    } else {
      return customer.CompanyName || '';
    }
  };

  const formatCustomerNameWithEnglish = (customer) => {
    // Add null/undefined check to prevent errors
    if (!customer) {
      return { thaiName: '', englishName: '' };
    }
    
    if (customer.CustomerType === 'Person') {
      const thaiName = `${customer.FirstName || ''} ${customer.LastName || ''}`.trim();
      const englishName = customer.FirstName_Eng && customer.LastName_Eng 
        ? `${customer.FirstName_Eng} ${customer.LastName_Eng}`
        : '';
      return { thaiName, englishName };
    } else {
      const thaiName = customer.CompanyName || '';
      const englishName = customer.CompanyName_Eng || '';
      return { thaiName, englishName };
    }
  };

  const formatSalespersonName = (salesperson) => {
    if (salesperson) {
      return `${salesperson.FirstName} ${salesperson.LastName}`;
    }
    return '';
  };

  const handleSalespersonClick = (salesperson) => {
    setShowSalespersonDetail(true);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/customers');
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(customerData?.customer);
    } else {
      navigate(`/customers/${id}/edit`);
    }
  };

  // Enrich flow: switch to edit form and auto-run enrichment so user can review before saving
  const handleEnrichData = () => {
    const customerId = customerData?.customer?.CustomerID || id;
    if (!customerId) {
      toast.error('Customer ID not found');
      return;
    }
    navigate(`/customers/${customerId}/edit?autoEnrich=1`);
  };

  // Lead Scout: Search for potential leads from customer updates
  const handleLeadScout = async () => {
    const customerId = customerData?.customer?.CustomerID || id;
    if (!customerId) {
      toast.error('Customer ID not found');
      return;
    }

    try {
      setScouting(true);
      setScoutResults(null);

      // Detect if customer primary name contains Thai characters
      const cust = customerData?.customer || {};
      const thaiRegex = /[\u0E00-\u0E7F]/;
      const thaiNamePresent = (
        (cust.FirstName && thaiRegex.test(cust.FirstName)) ||
        (cust.LastName && thaiRegex.test(cust.LastName)) ||
        (cust.CustomerName && thaiRegex.test(cust.CustomerName)) ||
        (cust.CompanyName && thaiRegex.test(cust.CompanyName)) ||
        (cust.PersonCompanyName && thaiRegex.test(cust.PersonCompanyName))
      );

      const data = await customerLeadScout(customerId, {
        languagePreference: thaiNamePresent ? 'th' : undefined
      });

      setScoutResults(data);
      setShowScoutResults(true);

      if (data.foundLeads && data.potentialLeads.length > 0) {
        toast.success(`Found ${data.potentialLeads.length} potential lead(s)!`);
      } else {
        toast.success('Search completed. No new leads found.');
      }

      try {
        await fetchCustomerDetails(customerId);
        console.log('✅ Customer data refreshed after scout');
      } catch (refreshError) {
        console.error('⚠️ Failed to refresh customer data:', refreshError);
        toast('Please refresh the page to see updated summary', { icon: 'ℹ️' });
      }

    } catch (error) {
      console.error('Lead scout error:', error);
      toast.error(error.message || 'Failed to scout for leads');
    } finally {
      setScouting(false);
    }
  };

  if (loading || configLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {configLoading ? 'Loading configuration...' : 'Loading customer details...'}
        </p>
      </div>
    );
  }

  if (error || configError) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
          {configError ? 'Configuration Error' : 'Error'}
        </h3>
        <p className="text-red-600 dark:text-red-400">{configError || error}</p>
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <p className="text-gray-600 dark:text-gray-400">{safeT('customers.customerNotFound')}</p>
      </div>
    );
  }

  const { customer: customerInfo, salesperson } = customerData || {};

  // Additional safety check for customerInfo
  if (!customerInfo) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <p className="text-gray-600 dark:text-gray-400">{safeT('customers.customerNotFound')}</p>
      </div>
    );
  }

  const customerAttributes = (() => {
    const raw = customerInfo.Attributes ?? customerInfo.attributes;
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.warn('CustomerDetail: Failed to parse attributes JSON', error);
        return {};
      }
    }
    return {};
  })();



  return (
    <div className="max-w-6xl mx-auto">
      {/* Header - Title and Actions in Same Line */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          {/* Title Section */}
          <div className="flex items-start space-x-2 sm:space-x-4 flex-1 min-w-0">
            <button
              onClick={handleBack}
              className="flex-shrink-0 p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 touch-target"
            >
              <ArrowLeft className="h-4 w-4 dark:text-gray-300" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">
                {formatCustomerNameWithEnglish(customerInfo).thaiName}
              </h1>
              {formatCustomerNameWithEnglish(customerInfo).englishName && (
                <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 font-medium break-words">
                  {formatCustomerNameWithEnglish(customerInfo).englishName}
                </p>
              )}
            </div>
          </div>
          
          {/* Action Buttons - Aligned with Title */}
          <div className="flex flex-wrap items-center gap-2">
            {customerInfo?.CustomerID && (
              <>
                <button
                  onClick={handleLeadScout}
                  disabled={scouting}
                  className="flex-1 sm:flex-none bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight"
                  title="Scout for potential leads from customer updates on social media and web"
                >
                  {scouting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Target className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="whitespace-nowrap">{scouting ? 'Scouting...' : 'Lead Scout'}</span>
                </button>
                <button
                  onClick={handleEnrichData}
                  disabled={enriching}
                  className="flex-1 sm:flex-none bg-cyan-600 text-white px-3 py-2 rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight"
                  title="Enrich customer data using AI and search"
                >
                  {enriching ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Sparkles className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="whitespace-nowrap">{enriching ? 'Enriching...' : 'Enrich Data'}</span>
                </button>
              </>
            )}
            <button
              onClick={handleEdit}
              className="flex-1 sm:flex-none bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 dark:from-teal-600 dark:to-cyan-600 dark:hover:from-teal-700 dark:hover:to-cyan-700 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-semibold touch-target no-tap-highlight shadow-md transition-all duration-200"
            >
              <Edit2 className="h-4 w-4 flex-shrink-0" />
              <span>{safeT('common.edit')}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Type & Quick Actions Combined */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{safeT('customers.customerType')}</h3>
                <div className="flex items-center space-x-2">
                  {customerInfo.CustomerType === 'Person' ? (
                    <>
                      <User className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200">
                        {safeT('customers.individual')}
                      </span>
                    </>
                  ) : (
                    <>
                      <Building className="h-5 w-5 text-green-500 dark:text-green-400" />
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                        {safeT('customers.company')}
                      </span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="flex flex-col gap-2 sm:gap-3">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => {
                      const name = formatCustomerName(customerInfo) || 'Customer';
                      const params = new URLSearchParams({
                        relatedEntityType: 'Customer',
                        relatedEntityId: String(customerInfo.CustomerID),
                        entityName: name
                      });
                      
                      // Auto-fill contact information from customer
                      if (customerInfo.CustomerType === 'Person') {
                        // For Person customers
                        const contactName = `${customerInfo.FirstName || ''} ${customerInfo.LastName || ''}`.trim();
                        if (contactName) params.set('contactName', contactName);
                        if (customerInfo.PersonEmail || customerInfo.Email) params.set('contactEmail', customerInfo.PersonEmail || customerInfo.Email);
                        if (customerInfo.PersonPhone || customerInfo.Phone) params.set('contactPhone', customerInfo.PersonPhone || customerInfo.Phone);
                        if (customerInfo.JobTitle) params.set('jobTitle', customerInfo.JobTitle);
                        
                        // Company info from person's associated company
                        const companyName = customerInfo.PersonCompanyName || customerInfo.CompanyName;
                        if (companyName) params.set('companyName', companyName);
                        const industry = customerInfo.PersonCompanyIndustry || customerInfo.Industry;
                        if (industry) params.set('industry', industry);
                        const website = customerInfo.PersonCompanyWebsite || customerInfo.Website;
                        if (website) params.set('website', website);
                      } else {
                        // For Company customers
                        if (customerInfo.CompanyName) params.set('companyName', customerInfo.CompanyName);
                        if (customerInfo.Industry) params.set('industry', customerInfo.Industry);
                        if (customerInfo.Website) params.set('website', customerInfo.Website);
                        // Use company contact info as default contact
                        if (customerInfo.CompanyEmail) params.set('contactEmail', customerInfo.CompanyEmail);
                        if (customerInfo.CompanyPhone) params.set('contactPhone', customerInfo.CompanyPhone);
                      }
                      
                      navigate(`/leads/new?${params.toString()}`);
                    }}
                    className="inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-md border border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30 text-sm transition-colors"
                    title={safeT('customers.createLead', 'Create Lead')}
                  >
                    <Target className="h-4 w-4" />
                    <span className="hidden sm:inline">{safeT('customers.createLead', 'Create Lead')}</span>
                    <span className="sm:hidden">Lead</span>
                  </button>
                  <button
                    onClick={() => {
                      const name = formatCustomerName(customerInfo) || 'Customer';
                      navigate(`/opportunities/new?relatedEntityType=Customer&relatedEntityId=${customerInfo.CustomerID}&entityName=${encodeURIComponent(name)}`);
                    }}
                    className="inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-md border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-sm transition-colors"
                    title={safeT('customers.createOpportunity', 'Create Opportunity')}
                  >
                    <TrendingUp className="h-4 w-4" />
                    <span className="hidden sm:inline">{safeT('customers.createOpportunity', 'Create Opportunity')}</span>
                    <span className="sm:hidden">Opportunity</span>
                  </button>
                  <button
                    onClick={() => {
                      const name = formatCustomerName(customerInfo) || 'Customer';
                      navigate(`/activities/new?relatedEntityType=Customer&relatedEntityId=${customerInfo.CustomerID}&entityName=${encodeURIComponent(name)}`);
                    }}
                    className="inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-md border border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-sm transition-colors"
                    title={safeT('customers.createActivity', 'Create Activity')}
                  >
                    <Calendar className="h-4 w-4" />
                    <span className="hidden sm:inline">{safeT('customers.createActivity', 'Create Activity')}</span>
                  <span className="sm:hidden">Activity</span>
                </button>
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
            <div className="flex items-center space-x-2 mb-4">
              {customerInfo.CustomerType === 'Person' ? (
                <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              ) : (
                <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {customerInfo.CustomerType === 'Person' ? safeT('customers.personalInfo') : safeT('customers.companyInfo')}
              </h3>
            </div>

            {customerInfo.CustomerType === 'Person' ? (
              <div className="space-y-6">
                {/* Profile Summary Header */}
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start space-x-3 min-w-0 flex-1">
                      {/* Avatar with person icon */}
                      <div className="h-12 w-12 flex-shrink-0 rounded-full bg-teal-600 dark:bg-teal-500 text-white flex items-center justify-center shadow">
                        <User className="h-6 w-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 break-words">
                          {(() => {
                            const nameParts = [];
                            if (customerInfo.FirstName) nameParts.push(customerInfo.FirstName);
                            if (customerInfo.LastName) nameParts.push(customerInfo.LastName);
                            let nameSection = nameParts.join(' ').trim();
                            
                            // Add nickname in parentheses if available
                            if (customerInfo.NickName && nameSection) {
                              nameSection += ` (${customerInfo.NickName})`;
                            } else if (customerInfo.NickName && !nameSection) {
                              nameSection = customerInfo.NickName;
                            }
                            
                            return nameSection || customerInfo.CustomerName || '';
                          })()}
                        </div>
                        {(customerInfo.FirstName_Eng || customerInfo.LastName_Eng) && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 break-words">
                            {`${customerInfo.FirstName_Eng || ''} ${customerInfo.LastName_Eng || ''}`.trim()}
                          </div>
                        )}
                        {customerInfo.JobTitle && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">{customerInfo.JobTitle}</div>
                        )}
                        {customerInfo.Department && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">{customerInfo.Department}</div>
                        )}
                      </div>
                    </div>
                    {/* Organization Chip (clickable) */}
                    {customerInfo.PersonCompanyID && (customerInfo.PersonCompanyName || customerInfo.CompanyName) && (
                      <button
                        onClick={() => handleCompanyClick(customerInfo.PersonCompanyID)}
                        className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors w-full sm:w-auto justify-center sm:justify-start flex-shrink-0"
                        title={safeT('customers.viewCompany', 'View Organization')}
                      >
                        <Building className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm font-medium truncate max-w-[200px] sm:max-w-[180px] md:max-w-[220px]">
                          {customerInfo.PersonCompanyName || customerInfo.CompanyName}
                        </span>
                      </button>
                    )}
                    {/* Duplicate Badge */}
                    {duplicatePersons.length > 0 && (
                      <button
                        onClick={() => setShowMergeModal(true)}
                        className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors w-full sm:w-auto justify-center sm:justify-start flex-shrink-0"
                        title={safeT('customers.duplicatesFound', 'Duplicate Customers Found')}
                      >
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm font-medium">
                          {duplicatePersons.length} {duplicatePersons.length === 1 ? safeT('customers.duplicate', 'Duplicate') : safeT('customers.duplicates', 'Duplicates')}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                

                {/* Personal Information Fields - Title, Nickname, First Name, Last Name, English Names, Email, Phone, Job Title */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!isFieldHidden('PersonTitle') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.title', 'Title')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{customerInfo.PersonTitle || '-'}</p>
                    </div>
                  )}
                  {!isFieldHidden('NickName') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.nickName', 'Nick Name')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{customerInfo.NickName || '-'}</p>
                    </div>
                  )}
                  {!isFieldHidden('FirstName') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.firstName')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{customerInfo.FirstName || '-'}</p>
                    </div>
                  )}
                  {!isFieldHidden('LastName') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.lastName')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{customerInfo.LastName || '-'}</p>
                    </div>
                  )}
                  {!isFieldHidden('FirstName_Eng') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.firstName')} ({safeT('common.english', 'English')})
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{customerInfo.FirstName_Eng || '-'}</p>
                    </div>
                  )}
                  {!isFieldHidden('LastName_Eng') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.lastName')} ({safeT('common.english', 'English')})
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{customerInfo.LastName_Eng || '-'}</p>
                    </div>
                  )}
                  {!isFieldHidden('PersonEmail') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.email')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        {(() => {
                          const email = customerInfo.PersonEmail || customerInfo.Email;
                          return email && email !== '-' && email.includes('@') ? (
                            <a 
                              href={`mailto:${email}`}
                              className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 hover:underline text-gray-900 dark:text-gray-100"
                              title={`Send email to ${email}`}
                            >
                              {email}
                            </a>
                          ) : (
                            <p className="text-gray-900 dark:text-gray-100">{email || '-'}</p>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {!isFieldHidden('PersonPhone') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.phone')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <p className="text-gray-900 dark:text-gray-100">{customerInfo.PersonPhone || customerInfo.Phone || '-'}</p>
                      </div>
                    </div>
                  )}
                  {!isFieldHidden('JobTitle') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.jobTitle', 'Job Title')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{customerInfo.JobTitle || '-'}</p>
                    </div>
                  )}
                  {!isFieldHidden('Department') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {safeT('customers.department', 'Department')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{customerInfo.Department || '-'}</p>
                    </div>
                  )}
                </div>

                {/* Organization */}
                {(customerInfo.CompanyName || customerInfo.CompanyName_Eng || customerInfo.PersonCompanyName || customerInfo.PersonCompanyName_Eng) && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-semibold text-green-900 dark:text-green-100 flex items-center">
                        <Building className="h-4 w-4 mr-2" />
                        🏢 {safeT('customers.company', 'Organization')}
                      </h4>
                      
                      {/* Add to Customers Button */}
                      {!companyCheck.loading && (
                        <div>
                          {!companyCheck.exists ? (
                            <button
                              onClick={() => setShowAddCompanyModal(true)}
                              className="bg-green-600 text-white px-3 py-1.5 rounded-lg flex items-center space-x-2 hover:bg-green-700 text-xs font-medium"
                            >
                              <Users className="h-3.5 w-3.5" />
                              <span>{safeT('customers.addToCustomers', 'Add to Customers')}</span>
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(customerInfo.CompanyName || customerInfo.PersonCompanyName) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {safeT('customers.companyNameThai', 'Organization Name (Thai)')}
                          </label>
                          {customerInfo.CustomerType === 'Person' && customerInfo.PersonCompanyID ? (
                            <p 
                              className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 cursor-pointer transition-colors"
                              onClick={() => handleCompanyClick(customerInfo.PersonCompanyID)}
                            >
                              {customerInfo.CompanyName || customerInfo.PersonCompanyName}
                            </p>
                          ) : (
                            <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyName || customerInfo.PersonCompanyName}</p>
                          )}
                        </div>
                      )}
                      {(customerInfo.CompanyName_Eng || customerInfo.PersonCompanyName_Eng) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {safeT('customers.companyNameEng', 'Organization Name (English)')}
                          </label>
                          {customerInfo.CustomerType === 'Person' && customerInfo.PersonCompanyID ? (
                            <p 
                              className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 cursor-pointer transition-colors"
                              onClick={() => handleCompanyClick(customerInfo.PersonCompanyID)}
                            >
                              {customerInfo.CompanyName_Eng || customerInfo.PersonCompanyName_Eng}
                            </p>
                          ) : (
                            <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyName_Eng || customerInfo.PersonCompanyName_Eng}</p>
                          )}
                        </div>
                      )}
                      {(customerInfo.Branch || customerInfo.PersonCompanyBranch) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {safeT('customers.branch', 'Branch')}
                          </label>
                          <p className="text-gray-900 dark:text-gray-100">{customerInfo.Branch || customerInfo.PersonCompanyBranch || '-'}</p>
                        </div>
                      )}
                      {(customerInfo.Industry || customerInfo.PersonCompanyIndustry) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Industry (อุตสาหกรรม)
                          </label>
                          <p className="text-gray-900 dark:text-gray-100">{customerInfo.Industry || customerInfo.PersonCompanyIndustry}</p>
                        </div>
                      )}
                      {(customerInfo.CompanyAddress || customerInfo.PersonCompanyAddress) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {safeT('customers.address')}
                          </label>
                          <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyAddress || customerInfo.PersonCompanyAddress || '-'}</p>
                        </div>
                      )}
                      {(customerInfo.City || customerInfo.PersonCompanyCity || customerInfo.Country || customerInfo.PersonCompanyCountry || customerInfo.TaxID || customerInfo.PersonCompanyTaxID) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {(customerInfo.City || customerInfo.PersonCompanyCity) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {safeT('customers.city')}
                              </label>
                              <p className="text-gray-900 dark:text-gray-100">{customerInfo.City || customerInfo.PersonCompanyCity || '-'}</p>
                            </div>
                          )}
                          {(customerInfo.Country || customerInfo.PersonCompanyCountry) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {safeT('customers.country')}
                              </label>
                              <p className="text-gray-900 dark:text-gray-100">{customerInfo.Country || customerInfo.PersonCompanyCountry || '-'}</p>
                            </div>
                          )}
                          {(customerInfo.TaxID || customerInfo.PersonCompanyTaxID) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {safeT('customers.taxID', 'Tax ID')}
                              </label>
                              <p className="text-gray-900 dark:text-gray-100">{customerInfo.TaxID || customerInfo.PersonCompanyTaxID || '-'}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {(customerInfo.CompanyPhone || customerInfo.PersonCompanyPhone || customerInfo.Website || customerInfo.PersonCompanyWebsite) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {(customerInfo.CompanyPhone || customerInfo.PersonCompanyPhone) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {safeT('customers.phone')}
                              </label>
                              <div className="flex items-center space-x-2">
                                <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyPhone || customerInfo.PersonCompanyPhone || '-'}</p>
                              </div>
                            </div>
                          )}
                          {(customerInfo.Website || customerInfo.PersonCompanyWebsite) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                {safeT('customers.website')}
                              </label>
                              <div className="flex items-center space-x-2">
                                <Globe className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                {customerInfo.Website || customerInfo.PersonCompanyWebsite ? (
                                  <a
                                    href={normalizeHrefUrl(customerInfo.Website || customerInfo.PersonCompanyWebsite)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 transition-colors"
                                  >
                                    {shortenUrlDisplay(customerInfo.Website || customerInfo.PersonCompanyWebsite)}
                                  </a>
                                ) : (
                                  <p className="text-gray-500 dark:text-gray-400">-</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            ) : (
              <div className="space-y-6">
                {/* Company Profile Summary Header */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Company Icon */}
                      <div className="h-12 w-12 rounded-full bg-green-600 dark:bg-green-500 text-white flex items-center justify-center text-lg font-semibold shadow">
                        <Building className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {customerInfo.CompanyName || '-'}
                        </div>
                        {customerInfo.CompanyName_Eng && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {customerInfo.CompanyName_Eng}
                          </div>
                        )}
                        {customerInfo.Branch && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{customerInfo.Branch}</div>
                        )}
                      </div>
                    </div>
                    {/* Associated Persons Count Chip */}
                    {associatedPersons.length > 0 && (
                      <button
                        onClick={() => {
                          const element = document.getElementById('associated-persons-section');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                        className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors"
                        title={safeT('customers.viewAssociatedPersons', 'View Associated Persons')}
                      >
                        <Users className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {associatedPersons.length} {associatedPersons.length === 1 ? safeT('customers.person') : safeT('customers.persons')}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                

                {/* Organization Details */}
                {hasVisibleFieldsInGroup(['CompanyName', 'CompanyName_Eng', 'Branch', 'Industry', 'CompanyAddress', 'City', 'Country', 'CompanyPhone', 'Website', 'CompanyEmail']) && (
                  <div className="space-y-4">
                    {/* Organization Name (moved into the same block) */}
                    {!isFieldHidden('CompanyName') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {safeT('customers.companyNameThai', 'Organization Name (Thai)')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyName || '-'}</p>
                      </div>
                    )}
                    {!isFieldHidden('CompanyName_Eng') && customerInfo.CompanyName_Eng && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {safeT('customers.companyNameEng', 'Organization Name (English)')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyName_Eng}</p>
                      </div>
                    )}
                    {!isFieldHidden('Branch') && customerInfo.Branch && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {safeT('customers.branch', 'Branch')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{customerInfo.Branch || '-'}</p>
                      </div>
                    )}
                    {!isFieldHidden('Industry') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {safeT('customers.industry')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{customerInfo.Industry || '-'}</p>
                      </div>
                    )}
                    {!isFieldHidden('CompanyAddress') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {safeT('customers.address')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyAddress || customerInfo.Address || '-'}</p>
                      </div>
                    )}
                    {hasVisibleFieldsInGroup(['City', 'Country', 'TaxID']) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {!isFieldHidden('City') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.city')}
                            </label>
                            <p className="text-gray-900 dark:text-gray-100">{customerInfo.City || '-'}</p>
                          </div>
                        )}
                        {!isFieldHidden('Country') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.country')}
                            </label>
                            <p className="text-gray-900 dark:text-gray-100">{customerInfo.Country || '-'}</p>
                          </div>
                        )}
                        {!isFieldHidden('TaxID') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.taxID', 'Tax ID')}
                            </label>
                            <p className="text-gray-900 dark:text-gray-100">{customerInfo.TaxID || '-'}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {hasVisibleFieldsInGroup(['CompanyPhone', 'Website', 'CompanyEmail']) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {!isFieldHidden('CompanyPhone') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.phone')}
                            </label>
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyPhone || customerInfo.PersonCompanyPhone || '-'}</p>
                            </div>
                          </div>
                        )}
                        {!isFieldHidden('Website') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.website')}
                            </label>
                            <div className="flex items-center space-x-2">
                              <Globe className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              {customerInfo.Website ? (
                                <a
                                  href={normalizeHrefUrl(customerInfo.Website)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 transition-colors"
                                >
                                  {shortenUrlDisplay(customerInfo.Website)}
                                </a>
                              ) : (
                                <p className="text-gray-500 dark:text-gray-400">-</p>
                              )}
                            </div>
                          </div>
                        )}
                        {!isFieldHidden('CompanyEmail') && customerInfo.CompanyEmail && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {safeT('customers.companyEmail', 'Company Email')}
                            </label>
                            <div className="flex items-center space-x-2">
                              <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              {customerInfo.CompanyEmail && customerInfo.CompanyEmail !== '-' && customerInfo.CompanyEmail.includes('@') ? (
                                <a 
                                  href={`mailto:${customerInfo.CompanyEmail}`}
                                  className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 hover:underline text-gray-900 dark:text-gray-100"
                                  title={`Send email to ${customerInfo.CompanyEmail}`}
                                >
                                  {customerInfo.CompanyEmail}
                                </a>
                              ) : (
                                <p className="text-gray-900 dark:text-gray-100">{customerInfo.CompanyEmail || '-'}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Customer Summary - Show for both Person and Company */}
          {!isFieldHidden('CustomerSummary') && customerInfo.CustomerSummary && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {safeT('customers.customerSummary', 'Customer Summary')}
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <div className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                  {isSummaryLong(customerInfo.CustomerSummary) && !summaryExpanded ? (
                    <>
                      {renderTextWithLinks(truncateSummary(customerInfo.CustomerSummary))}
                      <span className="text-gray-400 dark:text-gray-500">...</span>
                    </>
                  ) : (
                    renderTextWithLinks(customerInfo.CustomerSummary)
                  )}
                </div>
                {isSummaryLong(customerInfo.CustomerSummary) && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setSummaryExpanded(!summaryExpanded)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                    >
                      {summaryExpanded ? (
                        <>
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                          {safeT('common.showLess', 'Show Less')}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          {safeT('common.showMore', 'Show More')}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {customAttributeFields.length > 0 && (
            <CustomAttributesDisplay
              fields={customAttributeFields}
              attributes={customerAttributes}
              title={safeT('customers.additionalDataSection')}
              entityName="customer"
              onFieldsUpdated={async () => {
                // Reload customer data to refresh custom attributes
                if (propCustomer?.CustomerID) {
                  window.location.reload();
                }
              }}
            />
          )}

          {/* Dynamic Fields from Entity Config (fields not already hardcoded) */}
          {(() => {
            const dynamicFields = getDynamicDetailFields();
            if (dynamicFields.length === 0) return null;
            
            // Group fields by their group property
            const groupedFields = {};
            dynamicFields.forEach(field => {
              const group = field.group || field.ui?.group || 'Additional Information';
              if (!groupedFields[group]) groupedFields[group] = [];
              groupedFields[group].push(field);
            });
            
            return Object.entries(groupedFields).map(([groupName, fields]) => (
              <div key={groupName} className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {groupName}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fields.map(field => renderDynamicDetailField(field))}
                </div>
              </div>
            ));
          })()}

          {/* Associated Persons - Only show for Company customers */}
          {customerInfo.CustomerType === 'Company' && (
            <div id="associated-persons-section" className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {safeT('customers.associatedPersons', 'Associated Persons')}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={handleCreateNewPerson}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>{safeT('customers.createNewPerson', 'Create New Person')}</span>
                </button>
              </div>
              
              {loadingAssociatedPersons ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                </div>
              ) : associatedPersons.length > 0 ? (
                <div className="space-y-4">
                  {associatedPersons.map((person) => (
                    <div 
                      key={person.CustomerID} 
                      className="border border-blue-200 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
                      onClick={() => handlePersonClick(person)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Thai Name */}
                          <div className="mb-2">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                              {person.FirstName} {person.LastName}
                            </h4>
                            {/* English Name */}
                            {(person.FirstName_Eng || person.LastName_Eng) && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {person.FirstName_Eng} {person.LastName_Eng}
                              </p>
                            )}
                          </div>
                          
                          {/* Contact Information */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            {person.JobTitle && (
                              <div className="flex items-center space-x-2">
                                <Briefcase className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                <span className="text-gray-600 dark:text-gray-400">{person.JobTitle}</span>
                              </div>
                            )}
                            {person.Email && (
                              <div className="flex items-center space-x-2">
                                <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                <a 
                                  href={`mailto:${person.Email}`} 
                                  className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300"
                                >
                                  {person.Email}
                                </a>
                              </div>
                            )}
                            {person.Phone && (
                              <div className="flex items-center space-x-2">
                                <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                <a 
                                  href={`tel:${person.Phone}`} 
                                  className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300"
                                >
                                  {person.Phone}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Salesperson Info */}
                        {(person.SalespersonFirstName || person.SalespersonLastName) && (
                          <div className="text-right text-sm">
                            <div className="text-gray-500 dark:text-gray-400">
                              {safeT('customers.assignedSalesperson', 'Assigned to')}
                            </div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {person.SalespersonFirstName} {person.SalespersonLastName}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {safeT('customers.noAssociatedPersons', 'No associated persons found for this company')}
                  </p>
                  <button
                    type="button"
                    onClick={handleCreateNewPerson}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span>{safeT('customers.createFirstPerson', 'Create First Person')}</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Related sections removed - now shown in Entity Relations sidebar */}

          {/* Attachments */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Paperclip className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('customers.attachments', 'Attachments')}</h3>
            </div>
            <AttachmentServiceV2 
              entityType="Customer" 
              entityId={customerInfo.CustomerID} 
              onAttachmentChange={() => {
                // Optional: Refresh customer data or show notification
              }}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Entity Relations */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('common.relatedItems', 'Relationships')}</h3>
              </div>
              {entityRelationsUnified.length > 0 && (
                <span className="bg-teal-100 dark:bg-blue-900 text-teal-800 dark:text-teal-200 text-xs px-2 py-1 rounded-full">
                  {entityRelationsUnified.length}
                </span>
              )}
            </div>
            <div>
              {entityRelationsUnified.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">{safeT('customers.noEntityRelations', 'No entity relations')}</p>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {(() => {
                    // Group entities by type
                    const grouped = entityRelationsUnified.reduce((acc, rel) => {
                      const type = rel.EntityType;
                      if (!acc[type]) {
                        acc[type] = [];
                      }
                      acc[type].push(rel);
                      return acc;
                    }, {});

                    // Define display order: Customer first, then Activity, then others
                    const typeOrder = ['Customer', 'Activity', 'Lead', 'Opportunity', 'SaleNote'];
                    const sortedTypes = Object.keys(grouped).sort((a, b) => {
                      const aIndex = typeOrder.indexOf(a);
                      const bIndex = typeOrder.indexOf(b);
                      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                      if (aIndex === -1) return 1;
                      if (bIndex === -1) return -1;
                      return aIndex - bIndex;
                    });

                    return sortedTypes.map((entityType) => {
                      const entities = grouped[entityType];
                      // For Customer type, sort by CustomerType (Company first, then Person)
                      const sortedEntities = entityType === 'Customer' 
                        ? [...entities].sort((a, b) => {
                            const aType = a.CustomerType || 'Company';
                            const bType = b.CustomerType || 'Company';
                            if (aType === bType) return 0;
                            return aType === 'Company' ? -1 : 1;
                          })
                        : entities;

                      return (
                        <div key={entityType} className="space-y-2">
                          <div className="flex items-center space-x-2 mb-2">
                            {getEntityIcon(entityType)}
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                              {entityType} ({entities.length})
                            </h4>
                          </div>
                          <div className="space-y-2">
                            {sortedEntities.map((rel, idx) => {
                              const colors = getEntityColors(rel.EntityType, rel.CustomerType);
                              const isPersonCustomer = rel.EntityType === 'Customer' && rel.CustomerType === 'Person';
                              
                              return (
                                <div
                                  key={rel.RelationID || `${rel.EntityType}-${rel.EntityID}-${idx}`}
                                  className={`border ${colors.border} ${colors.bg} rounded-lg p-3 ${colors.hoverBorder} cursor-pointer transition-all ${
                                    isPersonCustomer ? 'ml-6' : ''
                                  }`}
                                  onClick={() => {
                                    const path = getEntityPath(rel.EntityType, rel.EntityID);
                                    if (path !== '#') navigate(path);
                                  }}
                                >
                                  <div className="flex items-start space-x-2">
                                    <div className="mt-0.5 flex-shrink-0">
                                      {getEntityIcon(rel.EntityType)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between mb-1">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate pr-2">{rel.EntityName}</p>
                                        <ArrowRight className={`h-3.5 w-3.5 ${colors.icon} flex-shrink-0 mt-0.5`} />
                                      </div>
                                      <div className="flex items-center space-x-2 mb-1">
                                        <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${colors.badge}`}>
                                          {rel.EntityType}
                                          {rel.CustomerType && ` (${rel.CustomerType === 'Person' ? 'Individual' : 'Company'})`}
                                        </span>
                                      {rel.Status && (
                                        <span className={`px-1.5 py-0.5 text-xs font-semibold rounded ${
                                          rel.EntityType === 'Lead' && rel.Status === 'New' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                          rel.EntityType === 'Lead' && rel.Status === 'Qualified' ? 'bg-teal-100 dark:bg-blue-900 text-teal-800 dark:text-teal-200' :
                                          rel.EntityType === 'Lead' && rel.Status === 'Converted' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' :
                                          rel.EntityType === 'Opportunity' && rel.Status === 'Closed Won' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                          rel.EntityType === 'Opportunity' ? 'bg-teal-100 dark:bg-blue-900 text-teal-800 dark:text-teal-200' :
                                          'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                        }`}>
                                          {rel.Status}
                                        </span>
                                      )}
                                      {rel.Amount && (
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{rel.Amount}</span>
                                      )}
                                    </div>
                                    {/* Description for Leads, SaleNotes, and other entities */}
                                    {rel.Description && (
                                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                                        {rel.Description}
                                      </p>
                                    )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
          {/* Salesperson Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <UserCheck className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{safeT('customers.salespersonInfo')}</h3>
            </div>
            {salesperson ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {safeT('customers.assignedSalesperson')}
                  </label>
                  <p 
                    className="text-gray-900 dark:text-gray-100 font-medium cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    onClick={() => handleSalespersonClick(salesperson)}
                    title="Click to view salesperson details"
                  >
                    {formatSalespersonName(salesperson)}
                  </p>
                </div>
                {salesperson.Email && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.email')}
                    </label>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <a href={`mailto:${salesperson.Email}`} className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 text-sm">
                        {salesperson.Email}
                      </a>
                    </div>
                  </div>
                )}
                {salesperson.Phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.phone')}
                    </label>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <a href={`tel:${salesperson.Phone}`} className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 text-sm">
                        {salesperson.Phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <Users className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">{safeT('customers.noSalesperson')}</p>
              </div>
            )}
          </div>

          {/* Tags Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Tag className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {safeT('common.tags', 'Tags')}
              </h3>
            </div>
            <TagService
              entityType="Customer"
              entityId={customerInfo.CustomerID}
              initialTags={customerInfo.Tags ? (typeof customerInfo.Tags === 'string' ? customerInfo.Tags.split(',').map(t => t.trim()).filter(Boolean) : customerInfo.Tags) : []}
              maxTags={15}
              onChange={async (newTags) => {
                // Refresh customer data after tag change
                await fetchCustomerDetails(customerInfo.CustomerID);
              }}
            />
          </div>

          {/* System Information (CustomerCode, CreatedAt, UpdatedAt) */}
          {(!isFieldHidden('CustomerCode') || !isFieldHidden('CreatedAt') || !isFieldHidden('UpdatedAt')) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {safeT('customers.systemInformation', 'System Information')}
                </h3>
              </div>
              <div className="space-y-3">
                {!isFieldHidden('CustomerCode') && customerInfo.CustomerCode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.customerCode', 'Customer Code')}
                    </label>
                    <p className="text-gray-900 dark:text-gray-100 font-mono">
                      {customerInfo.CustomerCode}
                    </p>
                  </div>
                )}
                {!isFieldHidden('CreatedAt') && customerInfo.CreatedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.createdAt', 'Created At')}
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">
                      {new Date(customerInfo.CreatedAt).toLocaleString()}
                    </p>
                  </div>
                )}
                {!isFieldHidden('UpdatedAt') && customerInfo.UpdatedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {safeT('customers.updatedAt', 'Updated At')}
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">
                      {new Date(customerInfo.UpdatedAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Company to Customers Modal */}
      {showAddCompanyModal && customerData?.customer && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">
                  {companyCheck.duplicates.length > 0 
                    ? safeT('customers.duplicateCompaniesFound', 'Duplicate Companies Found')
                    : safeT('customers.addCompanyToCustomers', 'Add Company to Customers')
                  }
                </h2>
                <button
                  onClick={() => setShowAddCompanyModal(false)}
                  className="text-gray-400 hover:text-gray-200 transition-colors"
                  disabled={addingCompany}
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {companyCheck.duplicates.length > 0 ? (
                <div className="space-y-6">
                  <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4">
                    <p className="text-sm text-yellow-200">
                      {safeT('customers.duplicateCompanyWarning', 'We found existing company customers that might match. You can view them or create a new entry.')}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-white">
                      {safeT('customers.existingCompanies', 'Existing Companies')}
                    </h3>
                    
                    {companyCheck.duplicates.map((duplicate) => (
                      <div 
                        key={duplicate.CustomerID} 
                        className="border border-gray-600 rounded-lg p-4 hover:border-teal-500 transition-colors bg-gray-700/50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="flex items-center space-x-2">
                                <Building className="h-5 w-5 text-gray-400" />
                                <span className="font-semibold text-white">
                                  {duplicate.CompanyName}
                                </span>
                              </div>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                duplicate.matchType === 'name' ? 'bg-blue-900/50 text-blue-200 border border-teal-700' :
                                'bg-green-900/50 text-green-200 border border-green-700'
                              }`}>
                                {duplicate.matchType === 'name' 
                                  ? safeT('customers.matchThaiName', 'Match: Thai Name')
                                  : safeT('customers.matchEngName', 'Match: English Name')
                                }
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-300">
                              {duplicate.CompanyName_Eng && (
                                <div className="flex items-center space-x-2">
                                  <Globe className="h-4 w-4" />
                                  <span>{duplicate.CompanyName_Eng}</span>
                                </div>
                              )}
                              {duplicate.Industry && (
                                <div className="flex items-center space-x-2">
                                  <Briefcase className="h-4 w-4" />
                                  <span>{duplicate.Industry}</span>
                                </div>
                              )}
                              {duplicate.Phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="h-4 w-4" />
                                  <span>{duplicate.Phone}</span>
                                </div>
                              )}
                              {duplicate.CustomerCode && (
                                <div className="flex items-center space-x-2">
                                  <FileText className="h-4 w-4" />
                                  <span>Code: {duplicate.CustomerCode}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <button
                            onClick={() => {
                              setShowAddCompanyModal(false);
                              navigate(`/customers/${duplicate.CustomerID}`);
                            }}
                            disabled={addingCompany}
                            className="ml-4 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
                          >
                            {safeT('customers.viewThis', 'View This')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-600 pt-6">
                    <h3 className="font-semibold text-lg text-white mb-3">
                      {safeT('customers.orCreateNew', 'Or Create New Company')}
                    </h3>
                    <p className="text-sm text-gray-300 mb-4">
                      {safeT('customers.createNewCompanyDescription', 'If this is a different company, you can create a new company customer record.')}
                    </p>
                    <button
                      onClick={handleCreateCompanyCustomer}
                      disabled={addingCompany}
                      className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                    >
                      {addingCompany ? safeT('common.processing', 'Processing...') : safeT('customers.createNewCompany', 'Create New Company')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-4">
                    <p className="text-sm text-green-200">
                      {safeT('customers.noDuplicateCompaniesFound', 'No duplicate companies found. This company will be added as a new customer.')}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-white">
                      {safeT('customers.companyDetails', 'Company Details')}
                    </h3>
                    
                    <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label className="font-medium text-gray-300">{safeT('customers.companyNameThai', 'Company Name (Thai)')}</label>
                          <p className="text-white">{customerInfo.CompanyName || customerInfo.PersonCompanyName || '-'}</p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">{safeT('customers.companyNameEng', 'Company Name (English)')}</label>
                          <p className="text-white">{customerInfo.CompanyName_Eng || customerInfo.PersonCompanyName_Eng || '-'}</p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">{safeT('customers.industry')}</label>
                          <p className="text-white">{customerInfo.Industry || customerInfo.PersonCompanyIndustry || '-'}</p>
                        </div>
                        {customerInfo.Website && (
                          <div>
                            <label className="font-medium text-gray-300">{safeT('customers.website')}</label>
                            <p className="text-white">{customerInfo.Website}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleCreateCompanyCustomer}
                    disabled={addingCompany}
                    className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                  >
                    {addingCompany ? safeT('common.processing', 'Processing...') : safeT('customers.createCompanyCustomer', 'Create Company Customer')}
                  </button>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-700 bg-gray-900/50">
              <button
                onClick={() => setShowAddCompanyModal(false)}
                disabled={addingCompany}
                className="w-full px-4 py-2 border border-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 hover:text-white transition-colors"
              >
                {safeT('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Scout Results Modal */}
      {showScoutResults && scoutResults && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Lead Scout Results</h2>
                </div>
                <button
                  onClick={() => setShowScoutResults(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Search query: <span className="font-medium">{scoutResults.searchQuery}</span>
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Potential Leads */}
              {scoutResults.potentialLeads && scoutResults.potentialLeads.length > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
                      Potential Leads ({scoutResults.potentialLeads.length})
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {scoutResults.potentialLeads.map((lead, idx) => (
                      <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-700 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">{lead.title}</h4>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            lead.priority === 'high' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                            lead.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                          }`}>
                            {lead.priority} priority
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{lead.description}</p>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3 mb-3">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Why this is a lead:</span> {lead.reasoning}
                          </p>
                        </div>
                        {lead.suggestedActions && lead.suggestedActions.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Suggested Actions:</p>
                            <ul className="list-disc list-inside space-y-1">
                              {lead.suggestedActions.map((action, actionIdx) => (
                                <li key={actionIdx} className="text-sm text-gray-600 dark:text-gray-400">{action}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {lead.estimatedValue && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">Estimated Value:</span> {lead.estimatedValue}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          {lead.sourceUrl && (
                            <a
                              href={lead.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-teal-600 dark:text-teal-400 hover:underline flex items-center space-x-1"
                            >
                              <Globe className="h-4 w-4" />
                              <span>View Source</span>
                            </a>
                          )}
                          <button
                            onClick={() => {
                              const name = formatCustomerName(customerInfo);
                              const params = new URLSearchParams({
                                relatedEntityType: 'Customer',
                                relatedEntityId: String(customerInfo.CustomerID),
                                entityName: name
                              });

                              if (lead.title) {
                                params.set('title', lead.title);
                              }
                              if (lead.description) {
                                params.set('description', lead.description);
                              }
                              if (lead.priority) {
                                params.set('priority', lead.priority);
                              }
                              if (lead.reasoning) {
                                params.set('reasoning', lead.reasoning);
                              }
                              if (lead.estimatedValue) {
                                params.set('estimatedValue', lead.estimatedValue);
                              }
                              if (lead.sourceUrl) {
                                params.set('sourceUrl', lead.sourceUrl);
                              }
                              if (lead.suggestedActions && lead.suggestedActions.length > 0) {
                                params.set('actions', lead.suggestedActions.join('||'));
                              }
                              if (!params.has('leadSource')) {
                                params.set('leadSource', 'Lead Scout');
                              }

                              navigate(`/leads/new?${params.toString()}`);
                            }}
                            className="bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 text-sm flex items-center space-x-1"
                          >
                            <Plus className="h-4 w-4" />
                            <span>Create Lead</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Significant Updates */}
              {scoutResults.significantUpdates && scoutResults.significantUpdates.length > 0 && (
                <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Activity className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                    <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                      Recent Updates ({scoutResults.significantUpdates.length})
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {scoutResults.significantUpdates.map((update, idx) => (
                      <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg border border-teal-200 dark:border-teal-700 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">{update.title}</h4>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            update.confidence === 'high' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            update.confidence === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                          }`}>
                            {update.confidence} confidence
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{update.summary}</p>
                        {update.relevance && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">Relevance:</span> {update.relevance}
                          </p>
                        )}
                        {update.date && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{update.date}</p>
                        )}
                        {update.url && (
                          <a
                            href={update.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-teal-600 dark:text-teal-400 hover:underline flex items-center space-x-1"
                          >
                            <Globe className="h-4 w-4" />
                            <span>Read More</span>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {(!scoutResults.potentialLeads || scoutResults.potentialLeads.length === 0) && 
               (!scoutResults.significantUpdates || scoutResults.significantUpdates.length === 0) && (
                <div className="text-center py-12">
                  <Target className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No New Leads Found</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    No recent updates or potential leads were identified for this customer at this time.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Total results checked: {scoutResults.totalResults || 0}
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={() => setShowScoutResults(false)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Salesperson Detail Modal */}
      {showSalespersonDetail && salesperson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/40 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Salesperson Details</h2>
                <button
                  onClick={() => setShowSalespersonDetail(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <UserCheck className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Full Name
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">{formatSalespersonName(salesperson)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        User ID
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">{salesperson.UserID}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <Phone className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
                    Contact Information
                  </h3>
                  <div className="space-y-4">
                    {salesperson.Email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Email Address
                        </label>
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <a 
                            href={`mailto:${salesperson.Email}`} 
                            className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 font-medium"
                          >
                            {salesperson.Email}
                          </a>
                        </div>
                      </div>
                    )}
                    {salesperson.Phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Phone Number
                        </label>
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <a 
                            href={`tel:${salesperson.Phone}`} 
                            className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 font-medium"
                          >
                            {salesperson.Phone}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Person Merge Modal */}
      <PersonMergeModal
        isOpen={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        currentCustomer={customerData?.customer}
        duplicates={duplicatePersons}
        onMergeComplete={(mergedCustomer) => {
          // Refresh customer data after merge
          setCustomerData({ customer: mergedCustomer });
          setDuplicatePersons([]);
          setShowMergeModal(false);
          toast.success(safeT('customers.mergeSuccess', 'Person records merged successfully'));
        }}
      />
    </div>
  );
};

export default CustomerDetail; 